import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { ENDPOINTS } from "@/api/application-api";
import { queryKeys } from "@/constants";
import { REQUESTS_STRUCTURED_SEARCH_ANCHOR_ID } from "@/constants/requestLogsUi";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useNewRequests } from "@/queries";
import { StructuredSearchbar, useStructuredSearchStore } from "@/components/StructuredSearch";
import { isSelectedEndTimeBeforeNow } from "@/components/StructuredSearch/utils/isSelectedEndTimeBeforeNow";
import { useAnalyticsQuery } from "@/components/RequestsAnalytics/analytics-query-context";
import { useOptionalRequestLogsTableRefresh } from "@/components/RequestsAnalytics/request-logs-table-refresh-context";
import { isWorkspaceRequestLogsTablePathname } from "@/utils/workspacePathMatchers";
import { displayErrorToast } from "@/utils/toast";
import {
  getStructuredQueryTrackingProperties,
  trackRequestsAnalyticsEvent,
} from "@/utils/requests-analytics-tracking";

/** Full-page or tab route that shows the workspace request logs table (AG Grid). */
const isRequestLogsTableRoute = (pathname: string) =>
  isWorkspaceRequestLogsTablePathname(pathname) || pathname.includes("/request-logs");

const RequestsAnalyticsSearchbar = observer(() => {
  const structuredSearchStore = useStructuredSearchStore();
  const queryClient = useQueryClient();
  const userToken = useUserToken();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const { pathname } = useLocation();
  const tableRefresh = useOptionalRequestLogsTableRefresh();
  const { isSearchEndpointRunning } = useAnalyticsQuery();
  const { data: newRequestsData } = useNewRequests();
  const hasNewData = (newRequestsData?.count_requests ?? 0) > 0;

  const analyticsFetchingCount = useIsFetching({
    queryKey: [queryKeys.requests, "analytics", userToken, workspaceId],
    exact: false,
  });

  const onRequestLogsRoute = isRequestLogsTableRoute(pathname);

  const handleManualRefresh = useCallback(() => {
    try {
      trackRequestsAnalyticsEvent("Requests Analytics Refresh Clicked", {
        page: "Requests & Analytics",
        has_new_data: hasNewData,
        ...getStructuredQueryTrackingProperties({
          filters: structuredSearchStore.filters,
          q: structuredSearchStore.q,
          dateRange: structuredSearchStore.dateRange,
        }),
      });

      if (onRequestLogsRoute) {
        tableRefresh?.triggerRefresh();
        return;
      }

      queryClient.setQueriesData([ENDPOINTS.new_requests, userToken, workspaceId], {
        count_requests: 0,
      });
      const now = new Date();
      const shouldAdvanceEnd = isSelectedEndTimeBeforeNow(structuredSearchStore.dateRange.to, now);
      if (shouldAdvanceEnd) {
        structuredSearchStore.setDateRange({
          from: structuredSearchStore.dateRange.from,
          to: now,
        });
      }
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.requests, "analytics", userToken, workspaceId],
        exact: false,
      });
    } catch {
      displayErrorToast("Error refreshing requests");
    }
  }, [
    hasNewData,
    onRequestLogsRoute,
    queryClient,
    structuredSearchStore,
    tableRefresh,
    userToken,
    workspaceId,
  ]);

  const isRefreshing = onRequestLogsRoute ? isSearchEndpointRunning : analyticsFetchingCount > 0;

  return (
    <div id={REQUESTS_STRUCTURED_SEARCH_ANCHOR_ID} className="pb-2 pt-3 sm:pt-4">
      <StructuredSearchbar
        className="w-full"
        showHeader={false}
        onRefresh={handleManualRefresh}
        isRefreshing={isRefreshing}
        hasNewData={hasNewData}
      />
    </div>
  );
});

export default RequestsAnalyticsSearchbar;
